// Simple pricing utilities
// Assumptions:
// - `basePrice` in destination data represents a 7-day package price (defaultWeek = 7)
// - price scales linearly with number of days
// - transport options apply a multiplier to the base price

const DEFAULT_WEEK_DAYS = 7;

export function calculatePrice(basePrice, days = DEFAULT_WEEK_DAYS, transportMultiplier = 1) {
  const daysFactor = days / DEFAULT_WEEK_DAYS;
  const raw = basePrice * daysFactor * transportMultiplier;
  return Math.round(raw);
}

export function formatCurrency(value) {
  const n = Number(value || 0);
  try {
    return n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
  } catch (e) {
    return `${n} €`;
  }
}

export function suggestTransports(days = DEFAULT_WEEK_DAYS, location = '') {
  // Return an array of transport options with name and multiplier
  // Use location to prioritize sensible transport options
  const loc = String(location).toLowerCase();

  // Short trips (<=3 days)
  if (days <= 3) {
    if (loc === 'europe') {
      return [
        { id: 'train', name: 'Train', multiplier: 0.7, eta: 'fast' },
        { id: 'bus', name: 'Bus', multiplier: 0.55, eta: 'flexible' },
        { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 0.95, eta: 'fastest' }
      ];
    }

    return [
      { id: 'bus', name: 'Bus', multiplier: 0.6, eta: 'flexible' },
      { id: 'train', name: 'Train', multiplier: 0.8, eta: 'fast' },
      { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 1.0, eta: 'fastest' }
    ];
  }

  // Medium trips (<=7 days)
  if (days <= 7) {
    if (loc === 'asia') {
      return [
        { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 1.0, eta: 'fastest' },
        { id: 'flight-business', name: 'Flight (Business)', multiplier: 1.6, eta: 'fastest' },
        { id: 'train', name: 'Train', multiplier: 0.9, eta: 'fast' }
      ];
    }

    return [
      { id: 'train', name: 'Train', multiplier: 0.85, eta: 'fast' },
      { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 1.0, eta: 'fastest' },
      { id: 'flight-business', name: 'Flight (Business)', multiplier: 1.6, eta: 'fastest' }
    ];
  }

  // Long trips (>7 days)
  if (loc === 'europe') {
    return [
      { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 1.0, eta: 'fastest' },
      { id: 'multi-modal', name: 'Multi-modal (Flight + Train)', multiplier: 1.1, eta: 'balanced' },
      { id: 'flight-business', name: 'Flight (Business)', multiplier: 1.6, eta: 'fastest' }
    ];
  }

  return [
    { id: 'flight-economy', name: 'Flight (Economy)', multiplier: 1.0, eta: 'fastest' },
    { id: 'flight-business', name: 'Flight (Business)', multiplier: 1.6, eta: 'fastest' },
    { id: 'multi-modal', name: 'Multi-modal (Flight + Train)', multiplier: 1.15, eta: 'balanced' }
  ];
}

export default { calculatePrice, suggestTransports, formatCurrency };
